import { useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import useEmployeeService from '../employeeService';

type PermissionsMap = Record<string, string[]>;

const useEmployeePermissionsMap = (aliases: Array<string | null | undefined>) => {
  const { getEmployeePermissions } = useEmployeeService();
  const [permissionsMap, setPermissionsMap] = useState<PermissionsMap>({});
  const previousAliasesRef = useRef<string[]>([]);

  const normalizedAliases = useMemo(
    () => ((aliases ?? []) as Array<string | null | undefined>).filter(Boolean) as string[],
    [aliases],
  );

  const stableAliases = useMemo(() => {
    if (isEqual(previousAliasesRef.current, normalizedAliases)) {
      return previousAliasesRef.current;
    }

    previousAliasesRef.current = normalizedAliases;
    return normalizedAliases;
  }, [normalizedAliases]);

  useEffect(() => {
    if (stableAliases.length === 0) {
      setPermissionsMap({});
      return;
    }

    let cancelled = false;

    (async () => {
      const results = await Promise.all(stableAliases.map(alias => getEmployeePermissions(alias).catch(() => [])));

      if (cancelled) return;

      const map = stableAliases.reduce<PermissionsMap>((acc, alias, index) => {
        acc[alias] = Array.isArray(results[index]) ? results[index] : [];
        return acc;
      }, {});

      setPermissionsMap(map);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableAliases]);

  return { permissionsMap };
};

export default useEmployeePermissionsMap;
